/**
 * Sessions Service
 *
 * Handles creating, updating, and listing chat sessions via the backend API.
 * Sessions link authenticated users to their AgentCore conversation session IDs,
 * enabling users to resume previous conversations.
 */

// Cached API base URL loaded from aws-exports.json
let SESSIONS_API_URL = ""

/**
 * Load the sessions API URL from aws-exports.json configuration.
 *
 * Falls back to a relative /api/sessions path for local development
 * when no explicit API URL is configured.
 *
 * @returns The full URL for the sessions API endpoint.
 */
async function loadApiUrl(): Promise<string> {
  if (SESSIONS_API_URL) {
    return SESSIONS_API_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    // VITE_API_URL env var takes precedence (used with npm run dev --host).
    // Falls back to apiUrl from aws-exports.json (CI-generated for deployed envs).
    // Last resort: relative path (will 404 locally but works behind CloudFront).
    const baseUrl = import.meta.env.VITE_API_URL || config.apiUrl || ""
    SESSIONS_API_URL = baseUrl
      ? `${baseUrl}/api/sessions`
      : "/api/sessions"
    return SESSIONS_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Sessions API URL not configured")
  }
}

export interface Session {
  userId: string
  sessionId: string
  agent?: string
  title: string
  createdAt: string
  lastModifiedAt: string
  expiresAt?: string
  /** Cumulative input tokens consumed across all exchanges in this session. */
  inputTokens?: number
  /** Cumulative output tokens generated across all exchanges in this session. */
  outputTokens?: number
  /** Cumulative total tokens (input + output) across all exchanges in this session. */
  totalTokens?: number
}

export interface CreateSessionPayload {
  sessionId: string
  firstMessage: string
  agent?: string
}

/**
 * Create a new session entry in the backend.
 *
 * Should be called when the user sends their first message in a new conversation.
 *
 * @param payload - The session ID and first message content.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The created session object.
 */
export async function createSession(
  payload: CreateSessionPayload,
  accessToken: string
): Promise<Session> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data = await response.json()
  return data.session
}

/**
 * Update the lastModifiedAt timestamp for an existing session.
 *
 * Should be called after each message exchange to keep the session's
 * recency ordering accurate.
 *
 * @param sessionId - The UUID of the session to update.
 * @param accessToken - Auth0 access token for API authentication.
 */
export async function updateSession(
  sessionId: string,
  accessToken: string
): Promise<void> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(apiUrl, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ sessionId }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }
}

/**
 * List all sessions for the authenticated user, sorted by most recently active.
 * @param agent - The name of the agent to retrieve sessions for
 * @param accessToken - Auth0 access token for API authentication.
 * @returns Array of session objects ordered by lastModifiedAt descending.
 */
export async function listSessions(
  agent: string,
  accessToken: string
): Promise<Session[]> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${agent}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data = await response.json()
  return data.sessions
}

/**
 * Check if a session still exists in the backend.
 *
 * Used to verify a session hasn't been deleted (e.g., from another tab)
 * before attempting to load its messages.
 *
 * @param sessionId - The UUID of the session to check.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns true if the session exists, false if it was deleted/expired.
 */
export async function checkSessionExists(
  sessionId: string,
  accessToken: string
): Promise<boolean> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${sessionId}/check`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  return response.ok
}

/**
 * Fetch the full session metadata for a single session from the backend.
 *
 * Calls the same /check endpoint but returns the parsed session object
 * rather than a boolean. Useful for retrieving up-to-date token usage
 * and other metadata.
 *
 * @param sessionId - The UUID of the session to retrieve.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The session object, or null if not found.
 */
export async function getSession(
  sessionId: string,
  accessToken: string
): Promise<Session | null> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${sessionId}/check`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    return null
  }

  const data = await response.json()
  return data.session
}

/**
 * Rename a session by updating its title in the backend.
 *
 * @param sessionId - The UUID of the session to rename.
 * @param title - The new title for the session.
 * @param accessToken - Auth0 access token for API authentication.
 */
export async function renameSession(
  sessionId: string,
  title: string,
  accessToken: string
): Promise<void> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${sessionId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ title }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }
}

/**
 * Delete a session from the backend.
 *
 * Removes the session metadata from DynamoDB. AgentCore Memory events
 * are not deleted (they expire naturally).
 *
 * @param sessionId - The UUID of the session to delete.
 * @param accessToken - Auth0 access token for API authentication.
 */
export async function deleteSession(
  sessionId: string,
  accessToken: string
): Promise<void> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${sessionId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }
}

export interface SessionMessage {
  role: "user" | "assistant"
  content: string
  timestamp: string
  segments?: Array<{
    type: "text" | "tool"
    content?: string
    toolCall?: {
      toolUseId: string
      name: string
      input: string
      status: string
      result?: string
    }
  }>
}

/**
 * Retrieve all messages for a session from AgentCore Memory.
 *
 * Returns the full conversation history including text and tool call segments,
 * suitable for directly populating the ChatMessages component state.
 *
 * @param sessionId - The UUID of the session to retrieve messages for.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns Array of messages in chronological order.
 */
export async function getSessionMessages(
  sessionId: string,
  accessToken: string
): Promise<SessionMessage[]> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${sessionId}/messages`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  const data = await response.json()
  return data.messages
}
